/**
 * Export Service — eksport wpisow WP mapy jako JSON (truncated, ~500KB-1MB).
 *
 * Niezalezny od Decision Engine. Jeden SELECT + transformacja + Blob download.
 * Truncation rules (per user): content_plaintext WYLACZONY. Reszta truncate do 200-300 chars.
 */

import { supabase } from "@/integrations/supabase/client";

interface RawPost {
  id: string;
  wp_post_id: number;
  title: string | null;
  slug: string | null;
  wp_url: string | null;
  status: string | null;
  published_at: string | null;
  content_plaintext: string | null;
  excerpt: string | null;
  extracted_entity: string | null;
  extracted_attribute: string | null;
  quality_recommendation: string | null;
  information_gain_score: number | null;
  humanization_risk_score: number | null;
  entity_anchoring_score: number | null;
  retrieval_ready: boolean | null;
  quality_audit_verdict: string | null;
  quality_audit_issues: unknown;
  seo_organic_traffic: number | null;
  seo_top_3_count: number | null;
  seo_top_10_count: number | null;
  seo_top_50_count: number | null;
  seo_content_decay: boolean | null;
  seo_lost_keywords_count: number | null;
  seo_has_cannibalization: boolean | null;
  cluster_fit: string | null;
  cluster_fit_confidence: number | null;
  cluster_fit_reason: string | null;
  cluster_fit_method: string | null;
  final_action: string | null;
  final_action_priority: string | null;
  final_action_reason: string | null;
  final_action_locked: boolean | null;
  final_action_user_override: boolean | null;
  matched_node_title?: string | null;
  match_status?: string | null;
  match_confidence?: number | null;
  match_reason?: string | null;
}

function truncate(s: string | null | undefined, n: number): string | null {
  if (!s) return null;
  return s.length > n ? s.slice(0, n) : s;
}

export interface WpExportResult {
  filename: string;
  size_bytes: number;
  posts_count: number;
}

type LogCb = (msg: string) => void;

export async function exportWpDataAsJson(
  mapId: string,
  options: { onLog?: LogCb } = {},
): Promise<WpExportResult> {
  options.onLog?.("Ładowanie metadanych mapy...");

  // Mapa
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, macro_statement, website_id, website:websites(url)")
    .eq("id", mapId)
    .maybeSingle();

  const m = map as {
    id: string;
    name: string | null;
    macro_statement: string | null;
    website_id: string | null;
    website?: { url: string } | null;
  } | null;
  if (!m || !m.website_id) throw new Error("Mapa nie ma przypisanego website_id");
  const websiteDomain = m.website?.url
    ?.replace(/^https?:\/\//, "")
    .replace(/\/$/, "") ?? "unknown";

  // Seedy
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: seeds } = await (supabase as any)
    .from("thematic_seeds")
    .select("name")
    .eq("map_id", mapId)
    .order("priority", { ascending: false });
  const seedsList = ((seeds || []) as Array<{ name: string | null }>)
    .map((s) => s.name?.trim())
    .filter(Boolean) as string[];

  // Top 30 entities (policzone z required_entity_ids)
  options.onLog?.("Liczenie top 30 entities...");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("required_entity_ids")
    .eq("map_id", mapId);

  const counts = new Map<string, number>();
  for (const n of (nodes || []) as Array<{ required_entity_ids: string[] | null }>) {
    const ids = Array.isArray(n.required_entity_ids) ? n.required_entity_ids : [];
    for (const id of ids) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  const topIds = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 30)
    .map(([id]) => id);

  let topEntities: string[] = [];
  if (topIds.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: entitiesData } = await (supabase as any)
      .from("thematic_entities")
      .select("id, canonical_name")
      .in("id", topIds);
    const byId = new Map<string, string>(
      ((entitiesData || []) as Array<{ id: string; canonical_name: string }>).map((e) => [
        e.id,
        e.canonical_name,
      ]),
    );
    topEntities = topIds.map((id) => byId.get(id) ?? "").filter(Boolean);
  }

  // Wpisy + join matched_node
  options.onLog?.("Ładowanie wpisów...");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: postsData } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, wp_post_id, title, slug, wp_url, status, published_at, content_plaintext, excerpt, extracted_entity, extracted_attribute, quality_recommendation, information_gain_score, humanization_risk_score, entity_anchoring_score, retrieval_ready, quality_audit_verdict, quality_audit_issues, seo_organic_traffic, seo_top_3_count, seo_top_10_count, seo_top_50_count, seo_content_decay, seo_lost_keywords_count, seo_has_cannibalization, cluster_fit, cluster_fit_confidence, cluster_fit_reason, cluster_fit_method, final_action, final_action_priority, final_action_reason, final_action_locked, final_action_user_override",
    )
    .eq("website_id", m.website_id)
    .order("published_at", { ascending: false });

  const posts = (postsData || []) as RawPost[];

  // Join matched node per wpis (jedno query)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: matchNodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("matched_wp_post_id, title, match_status, match_confidence, match_reason")
    .eq("map_id", mapId)
    .not("matched_wp_post_id", "is", null);

  const matchByPostId = new Map<
    string,
    { title: string; match_status: string | null; match_confidence: number | null; match_reason: string | null }
  >();
  for (const n of (matchNodes || []) as Array<{
    matched_wp_post_id: string;
    title: string;
    match_status: string | null;
    match_confidence: number | null;
    match_reason: string | null;
  }>) {
    matchByPostId.set(n.matched_wp_post_id, {
      title: n.title,
      match_status: n.match_status,
      match_confidence: n.match_confidence,
      match_reason: n.match_reason,
    });
  }

  // Distribution summary — rozbudowane
  const FINAL_ACTIONS = [
    "keep",
    "refurbish",
    "refurbish_url_preserve",
    "keep_outer",
    "delete",
    "consolidate_301",
    "monitor",
  ] as const;

  const dist = {
    quality: { keep: 0, borderline: 0, refurbish: 0, null: 0 },
    cluster_fit: { in_scope: 0, adjacent: 0, off_topic: 0, null: 0 },
    cluster_fit_by_method: {
      auto_matched: 0,
      llm: 0,
      manual_override: 0,
      null: 0,
    },
    final_action: Object.fromEntries(
      FINAL_ACTIONS.map((a) => [a, 0]).concat([["null", 0]]),
    ) as Record<string, number>,
    final_action_detailed: Object.fromEntries(
      FINAL_ACTIONS.map((a) => [
        a,
        {
          total: 0,
          locked: 0,
          pending: 0,
          user_override: 0,
          without_matched_node: 0,
          by_priority: { high: 0, medium: 0, low: 0 },
        },
      ]),
    ) as Record<
      string,
      {
        total: number;
        locked: number;
        pending: number;
        user_override: number;
        without_matched_node: number;
        by_priority: { high: number; medium: number; low: number };
      }
    >,
    final_action_by_priority: {
      high: 0,
      medium: 0,
      low: 0,
      null: 0,
    },
    matching_stats: {
      total_posts: posts.length,
      with_matched_node: 0,
      without_matched_node: 0,
      match_status_breakdown: {
        matched_keep: 0,
        matched_refurbish: 0,
        partial: 0,
        unmatched: 0,
        skip_manual: 0,
        unchecked: 0,
        null: 0,
      } as Record<string, number>,
    },
    sro_stats: {
      extracted: 0,
      missing: 0,
    },
    audit_stats: {
      audited: 0,
      not_audited: 0,
      retrieval_ready_true: 0,
      retrieval_ready_false: 0,
    },
    seo: {
      with_traffic: 0,
      ranking: 0,
      top_3: 0,
      top_10: 0,
      decay: 0,
      cannib: 0,
      total_traffic_sum: 0,
    },
    action_logs_summary: {
      total_logged: 0,
      success: 0,
      failed: 0,
    },
  };

  for (const p of posts) {
    const qr = p.quality_recommendation ?? "null";
    if (qr in dist.quality) (dist.quality as Record<string, number>)[qr]++;

    const cf = p.cluster_fit ?? "null";
    if (cf in dist.cluster_fit) (dist.cluster_fit as Record<string, number>)[cf]++;

    const cfMethod = p.cluster_fit_method ?? "null";
    if (cfMethod in dist.cluster_fit_by_method) {
      (dist.cluster_fit_by_method as Record<string, number>)[cfMethod]++;
    }

    const fa = p.final_action ?? "null";
    dist.final_action[fa] = (dist.final_action[fa] ?? 0) + 1;

    if (p.final_action && p.final_action in dist.final_action_detailed) {
      const d = dist.final_action_detailed[p.final_action];
      d.total++;
      if (p.final_action_locked) d.locked++;
      else d.pending++;
      if (p.final_action_user_override) d.user_override++;
      const hasMatch = matchByPostId.has(p.id);
      if (!hasMatch) d.without_matched_node++;
      const pr = p.final_action_priority ?? null;
      if (pr === "high") d.by_priority.high++;
      else if (pr === "medium") d.by_priority.medium++;
      else if (pr === "low") d.by_priority.low++;
    }

    const priority = p.final_action_priority ?? "null";
    if (priority in dist.final_action_by_priority) {
      (dist.final_action_by_priority as Record<string, number>)[priority]++;
    }

    // matching
    const match = matchByPostId.get(p.id);
    if (match) {
      dist.matching_stats.with_matched_node++;
      const ms = match.match_status ?? "null";
      if (ms in dist.matching_stats.match_status_breakdown) {
        dist.matching_stats.match_status_breakdown[ms]++;
      } else {
        dist.matching_stats.match_status_breakdown.null++;
      }
    } else {
      dist.matching_stats.without_matched_node++;
      dist.matching_stats.match_status_breakdown.null++;
    }

    // SRO
    if (p.extracted_entity) dist.sro_stats.extracted++;
    else dist.sro_stats.missing++;

    // audit
    if (p.entity_anchoring_score !== null && p.entity_anchoring_score !== undefined) {
      dist.audit_stats.audited++;
      if (p.retrieval_ready) dist.audit_stats.retrieval_ready_true++;
      else dist.audit_stats.retrieval_ready_false++;
    } else {
      dist.audit_stats.not_audited++;
    }

    // SEO
    if ((p.seo_organic_traffic ?? 0) > 0) dist.seo.with_traffic++;
    if ((p.seo_top_50_count ?? 0) > 0) dist.seo.ranking++;
    if ((p.seo_top_3_count ?? 0) >= 1) dist.seo.top_3++;
    if ((p.seo_top_10_count ?? 0) >= 1) dist.seo.top_10++;
    if (p.seo_content_decay) dist.seo.decay++;
    if (p.seo_has_cannibalization) dist.seo.cannib++;
    dist.seo.total_traffic_sum += p.seo_organic_traffic ?? 0;
  }

  // Action logs summary (LUKA #2 tracking)
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: logs } = await (supabase as any)
      .from("wp_action_logs")
      .select("result")
      .eq("website_id", m.website_id);
    const logRows = (logs || []) as Array<{ result: string }>;
    dist.action_logs_summary.total_logged = logRows.length;
    dist.action_logs_summary.success = logRows.filter((l) => l.result === "success").length;
    dist.action_logs_summary.failed = logRows.filter((l) => l.result === "failed").length;
  } catch {
    // tabela moze nie istniec — ignoruj
  }

  // Transform posts
  const exportPosts = posts.map((p) => {
    const match = matchByPostId.get(p.id);
    const issues = Array.isArray(p.quality_audit_issues)
      ? (p.quality_audit_issues as string[]).slice(0, 3).map((i) => truncate(i, 200))
      : [];
    return {
      id: p.id,
      wp_post_id: p.wp_post_id,
      title: p.title,
      slug: p.slug,
      wp_url: p.wp_url,
      status: p.status,
      published_at: p.published_at,
      content_excerpt: truncate(p.content_plaintext ?? p.excerpt ?? "", 200),
      sro: {
        extracted_entity: p.extracted_entity,
        extracted_attribute: p.extracted_attribute,
      },
      quality: {
        recommendation: p.quality_recommendation,
        ig_score: p.information_gain_score,
        humanization_risk: p.humanization_risk_score,
        entity_anchoring: p.entity_anchoring_score,
        retrieval_ready: p.retrieval_ready,
        verdict_excerpt: truncate(p.quality_audit_verdict, 300),
        critical_issues: issues,
      },
      seo: {
        organic_traffic: p.seo_organic_traffic,
        top_3: p.seo_top_3_count,
        top_10: p.seo_top_10_count,
        top_50: p.seo_top_50_count,
        content_decay: p.seo_content_decay,
        lost_keywords: p.seo_lost_keywords_count,
        has_cannibalization: p.seo_has_cannibalization,
      },
      cluster_fit: {
        classification: p.cluster_fit,
        confidence: p.cluster_fit_confidence,
        reason_excerpt: truncate(p.cluster_fit_reason, 300),
        method: p.cluster_fit_method,
      },
      final_action: {
        action: p.final_action,
        priority: p.final_action_priority,
        reason_excerpt: truncate(p.final_action_reason, 300),
        locked: p.final_action_locked,
        user_override: p.final_action_user_override,
      },
      matching: match
        ? {
            match_status: match.match_status,
            matched_node_title: match.title,
            confidence: match.match_confidence,
            match_reason: truncate(match.match_reason, 200),
          }
        : {
            match_status: null,
            matched_node_title: null,
            confidence: null,
            match_reason: null,
          },
    };
  });

  const payload = {
    export_meta: {
      exported_at: new Date().toISOString(),
      website_domain: websiteDomain,
      map_id: m.id,
      map_name: m.name,
      map_macro_statement: m.macro_statement,
      total_posts: posts.length,
    },
    map_context: {
      seeds: seedsList.map((name) => ({ name })),
      top_30_entities: topEntities,
    },
    distribution_summary: dist,
    posts: exportPosts,
  };

  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const sizeBytes = blob.size;

  // Filename: domain-export-YYYY-MM-DD-HHMM.json
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const filename = `${websiteDomain.replace(/\W+/g, "-")}-export-${stamp}.json`;

  options.onLog?.(
    `Generowanie pliku ${filename} (${(sizeBytes / 1024).toFixed(0)} KB, ${posts.length} wpisów)`,
  );

  // Trigger download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return {
    filename,
    size_bytes: sizeBytes,
    posts_count: posts.length,
  };
}
